/**
 * WebSocket Notification API
 * Provides methods to manage notifications via WebSocket
 */

import websocketService from '../websocket';
import { createWebSocketRequest } from './utils';

/**
 * Get user notifications
 * @returns {Promise<Array>} - Promise resolving to the notification array
 */
export const getUserNotifications = async () => {
  return createWebSocketRequest({
    eventName: 'get-user-notifications-ack',
    requestType: 'get-user-notifications',
    requestPayload: {},
    dataValidator: (data) => {
      console.log('📬 Received get-user-notifications-ack:', data);
      return data?.data || [];
    },
    errorMessage: 'Timeout while getting notifications',
    timeout: 10000
  });
};

/**
 * Mark a notification as read
 * @param {string} notificationId - ID of the notification to mark as read
 * @returns {Promise<Object>} - Promise resolving when notification is marked as read
 */
export const markAsRead = async (notificationId) => {
  if (!notificationId) {
    throw new Error('Notification ID is required');
  }
  
  return createWebSocketRequest({
    eventName: 'mark-as-read-ack',
    requestType: 'mark-as-read',
    requestPayload: {
      data: {
        notificationId
      }
    },
    dataValidator: (data) => data?.data || { message: 'Notification marked as read' },
    errorMessage: 'Timeout while marking notification as read',
    timeout: 5000
  });
};

/**
 * Mark all notifications as read
 * @returns {Promise<Object>} - Promise resolving when all notifications are marked as read
 */
export const markAllAsRead = async () => {
  return createWebSocketRequest({
    eventName: 'mark-all-as-read-ack',
    requestType: 'mark-all-as-read',
    dataValidator: (data) => data?.data || { message: 'All notifications marked as read' },
    errorMessage: 'Timeout while marking all notifications as read',
    timeout: 5000
  });
};

/**
 * Remove a notification
 * @param {string} notificationId - ID of the notification to remove
 * @returns {Promise<Object>} - Promise resolving when notification is removed
 */
export const removeNotification = async (notificationId) => {
  if (!notificationId) {
    throw new Error('Notification ID is required');
  }
  
  return createWebSocketRequest({
    eventName: 'remove-notification-ack',
    requestType: 'remove-notification',
    requestPayload: {
      data: {
        notificationId
      }
    },
    dataValidator: (data) => data?.data || { message: 'Notification removed' },
    errorMessage: 'Timeout while removing notification',
    timeout: 5000
  });
};

// Export an event subscription helper
export const subscribeToNotifications = (callback) => {
  // Subscribe to new notifications
  websocketService.on('new-notification', callback);
  
  // Return unsubscribe function
  return () => {
    websocketService.off('new-notification', callback);
  };
}; 